import {InvoiceRowForm} from './_models'
import {InvoiceSubject} from './invoiceSubject'

// الگوی ۱۱ (بورس اوراق بهادار مبتنی بر کالا) طبق RC_IITP.IS_V7.9:
// «مبلغ بعد از تخفیف» (adis) باید عیناً برابر ارزش معامله در اعلامیه فروش بورس باشد و
// مبلغ واحد، مبلغ قبل از تخفیف، تخفیف، سایر مالیات/وجوه قانونی، ارز و پرداخت‌ها خارج از الگو هستند.

export const TEMPLATE_11_ALLOWED_SUBJECTS: InvoiceSubject[] = [1, 2, 3]
export const TEMPLATE_11_MAX_SIGNIFICANT_DIGITS = 15
export const TEMPLATE_11_TOO_LARGE_ERROR = 'مبلغ یا تعداد برای الگوی بورس بیش از حد بزرگ است.'
export const TEMPLATE_11_ADIS_WHOLE_NUMBER_ERROR =
  'مبلغ بعد از تخفیف باید عدد صحیح و بزرگ‌تر از صفر (ریال) باشد.'

export const isTemplate11 = (invoiceType: unknown, invoiceTemplate: unknown) =>
  Number(invoiceType) === 1 && Number(invoiceTemplate) === 11

const ZERO = BigInt(0)
const ONE = BigInt(1)
const TWO = BigInt(2)
const TEN = BigInt(10)
// C# decimal guard in ResolveTemplate11UnitAmount: `midpoint >= 1e27m / scale`
const DECIMAL_GUARD = BigInt('1000000000000000000000000000')

const pow10 = (exponent: number) => {
  let result = ONE
  for (let index = 0; index < exponent; index++) result *= TEN
  return result
}

type ExactDecimal = {digits: bigint; scale: number} // value = digits / 10^scale

// Parses a non-negative decimal (string or number, commas allowed, exponent allowed) exactly.
const parseExactDecimal = (value: unknown): ExactDecimal | undefined => {
  if (value === null || value === undefined) return undefined
  if (typeof value === 'number' && !Number.isFinite(value)) return undefined
  const text = String(value).trim().replace(/,/g, '')
  const match = /^(\d*)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/.exec(text)
  if (!match) return undefined
  const integerPart = match[1] || ''
  const fractionPart = match[2] || ''
  if (integerPart === '' && fractionPart === '') return undefined

  let digits = BigInt(integerPart + fractionPart || '0')
  let scale = fractionPart.length - Number(match[3] || 0)
  if (scale < 0) {
    digits *= pow10(-scale)
    scale = 0
  }
  return {digits, scale}
}

const formatUnits = (units: bigint, decimals: number) => {
  const raw = units.toString().padStart(decimals + 1, '0')
  if (decimals === 0) return raw
  const integerPart = raw.slice(0, raw.length - decimals)
  const fractionPart = raw.slice(raw.length - decimals).replace(/0+$/, '')
  return fractionPart ? `${integerPart}.${fractionPart}` : integerPart
}

export const countSignificantDigits = (decimalText: string) =>
  decimalText.replace('.', '').replace(/^0+/, '').replace(/0+$/, '').length

const isBrowserSafe = (decimalText: string) =>
  countSignificantDigits(decimalText) <= TEMPLATE_11_MAX_SIGNIFICANT_DIGITS

// Backend قدیمی: Math.Round(amount, 8, MidpointRounding.AwayFromZero) و سپس floor(amount × qty).
const roundTo8AwayFromZero = (value: ExactDecimal): ExactDecimal => {
  if (value.scale <= 8) return value
  const drop = pow10(value.scale - 8)
  const quotient = value.digits / drop
  return {digits: TWO * (value.digits % drop) >= drop ? quotient + ONE : quotient, scale: 8}
}

// عددی که واقعاً ارسال می‌شود JSON.stringify(Number(text)) است. تا ۱۵ رقم معنادار همان text است؛
// بیشتر از آن ممکن است رقم آخر جابه‌جا شود و باید هنوز floor(round8(ارسالی) × qty) همان adis بماند
// (همان کنترل SurvivesBrowserNumber در Backend، اینجا دقیق روی مقدار واقعی ارسالی).
const survivesPost = (text: string, amountAfterDiscount: bigint, quantity: ExactDecimal) => {
  if (isBrowserSafe(text)) return true
  const posted = parseExactDecimal(JSON.stringify(Number(text)))
  if (!posted) return false
  const rounded = roundTo8AwayFromZero(posted)
  return (rounded.digits * quantity.digits) / pow10(rounded.scale + quantity.scale) === amountAfterDiscount
}

/**
 * Port of MegaPayBackend InvoicesController.ResolveTemplate11UnitAmount with exact rational
 * arithmetic. Returns the first unit amount (decimals 0..8, ceiling before the half-away-from-zero
 * midpoint) whose floor(unit × qty) equals adis exactly and whose posted value survives: up to 15
 * significant digits Number/JSON.stringify keep it exactly; above that the value actually posted
 * (JSON.stringify(Number(result))), rounded to 8 decimals like the backend, must still floor to adis.
 * Returns undefined when adis is not a whole non-negative number that a JS number holds exactly
 * (adis itself is posted), qty is invalid, or no such candidate exists (qty ≳ 1e8 or adis ≳ 9e15).
 */
export const resolveTemplate11UnitAmount = (
  adis: string | number,
  qty: string | number
): string | undefined => {
  const amount = parseExactDecimal(adis)
  const quantity = parseExactDecimal(qty)
  if (!amount || !quantity) return undefined

  const amountScale = pow10(amount.scale)
  if (amount.digits % amountScale !== ZERO) return undefined
  const amountAfterDiscount = amount.digits / amountScale
  // adis خودش عیناً ارسال و در Backend جدید ذخیره می‌شود؛ عددی که Number دقیق نگه نمی‌دارد (بیش از 2^53) پذیرفته نیست.
  const postedAdis = parseExactDecimal(JSON.stringify(Number(amountAfterDiscount.toString())))
  if (!postedAdis || postedAdis.scale !== 0 || postedAdis.digits !== amountAfterDiscount) return undefined

  if (quantity.digits === ZERO) {
    const text = amountAfterDiscount.toString()
    return isBrowserSafe(text) ? text : undefined
  }

  const qtyNumerator = quantity.digits
  const qtyDenominator = pow10(quantity.scale)
  // lowerBound = adis / qty ; midpoint = (adis + 0.5) / qty
  const lowerBoundNumerator = amountAfterDiscount * qtyDenominator
  const midpointNumerator = (TWO * amountAfterDiscount + ONE) * qtyDenominator
  const midpointDenominator = TWO * qtyNumerator

  let scale = ONE
  for (let decimals = 0; decimals <= 8; decimals++) {
    if (midpointNumerator * scale >= midpointDenominator * DECIMAL_GUARD) break

    const scaledLowerBound = lowerBoundNumerator * scale
    const ceilingUnits = (scaledLowerBound + qtyNumerator - ONE) / qtyNumerator
    const scaledMidpoint = midpointNumerator * scale
    const nearMidpointUnits =
      (TWO * scaledMidpoint + midpointDenominator) / (TWO * midpointDenominator)

    for (const units of [ceilingUnits, nearMidpointUnits]) {
      if ((units * qtyNumerator) / (scale * qtyDenominator) !== amountAfterDiscount) continue
      const text = formatUnits(units, decimals)
      if (survivesPost(text, amountAfterDiscount, quantity)) return text
    }
    scale *= TEN
  }

  return undefined
}

export const isValidTemplate11Adis = (value: unknown) => {
  const text = String(value ?? '').trim()
  return /^\d+$/.test(text) && /[1-9]/.test(text)
}

const finiteOrUndefined = (value: unknown) => {
  if (value === null || value === undefined || value === '') return undefined
  const number = Number(value)
  return Number.isFinite(number) ? number : undefined
}

export const getTemplate11RowAdis = (row: InvoiceRowForm) =>
  finiteOrUndefined(row.adis) ?? finiteOrUndefined(row.adPrice) ?? finiteOrUndefined(row.price)

export const getTemplate11RowsError = (rows: InvoiceRowForm[] = []) => {
  for (let index = 0; index < rows.length; index++) {
    const row = rows[index]
    const adis = getTemplate11RowAdis(row)
    if (adis === undefined || !isValidTemplate11Adis(adis)) {
      return `ردیف ${index + 1}: ${TEMPLATE_11_ADIS_WHOLE_NUMBER_ERROR}`
    }
    const qty = finiteOrUndefined(row.qty)
    if (qty === undefined || qty <= 0) {
      return `ردیف ${index + 1}: تعداد / مقدار باید بزرگ‌تر از صفر باشد.`
    }
    if (resolveTemplate11UnitAmount(adis, qty) === undefined) {
      return `ردیف ${index + 1}: ${TEMPLATE_11_TOO_LARGE_ERROR}`
    }
  }
  return ''
}

// ردیف الگوی ۱۱ را برای ارسال آماده می‌کند: adis عیناً ارسال می‌شود و amount طوری محاسبه می‌شود که
// Backend قدیمی (floor(amount × qty) − discount) هم دقیقاً همان adis را ذخیره کند.
export const toTemplate11SubmitRow = (row: InvoiceRowForm): InvoiceRowForm => {
  const adis = getTemplate11RowAdis(row)
  const qty = finiteOrUndefined(row.qty)
  const unitAmount =
    adis !== undefined && qty !== undefined && qty > 0
      ? resolveTemplate11UnitAmount(adis, qty)
      : undefined

  return {
    ...row,
    adis,
    amount: unitAmount !== undefined ? Number(unitAmount) : row.amount,
    discount: 0,
    totalDiscount: 0,
    price: adis,
    adPrice: adis,
    netPrice: adis,
    vatRate: 0,
    vatRateManuallyChanged: false,
    vatPrice: 0,
    taxName: undefined,
    taxRate: undefined,
    taxPrice: 0,
    govTaxName: undefined,
    govTaxRate: undefined,
    govTaxPrice: 0,
    currency: undefined,
    currencyId: undefined,
    currencyRate: undefined,
    amountCurrency: undefined,
    currencyValue: undefined,
    currencyPurchaseRate: undefined,
    vatBaseAmount: undefined,
    weight: undefined,
    contractNo: undefined,
  }
}

// روش تسویه ۳ (نقد و نسیه) در الگوی ۱۱: سهم نقدی (cap) الزامی است و cap + insp = مبلغ کل.
export const getTemplate11CashError = (settlement: string, cash: string, total: number) => {
  if (settlement !== '3') return ''
  const text = String(cash ?? '').trim()
  if (!/^\d+$/.test(text)) return 'سهم نقدی را به ریال و به‌صورت عدد صحیح وارد کنید.'
  const value = Number(text)
  if (!(value > 0)) return 'سهم نقدی باید بزرگ‌تر از صفر باشد.'
  if (!(value < total)) return 'سهم نقدی باید کمتر از مبلغ کل صورتحساب باشد.'
  return ''
}
